import { Children, forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import Label from './Label';
import SimpleAccordion from './SimpleAccordion';
import type { AccordionProps, AccordionRefObject } from './interfaces';

type Props = AccordionProps;

const Accordion = forwardRef<AccordionRefObject, Props>(
  (
    {
      classes = {},
      collapsed = false,
      grows = true,
      height = 60,
      label = '',
      showCount = true,
      tooltipText = '',
      ...props
    },
    ref
  ) => {
    const count = Children.count(props.children);
    const [prevCount, setPrevCount] = useState(count);
    const [open, setOpen] = useState(!collapsed);

    // React-recommended pattern for responding to prop changes without an effect:
    // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    if (count !== prevCount) {
      setPrevCount(count);
      if (!collapsed && count > 0) {
        setOpen(true);
      }
    }

    const toggleVisibility = useCallback(() => {
      setOpen((prev) => (count > 0 ? !prev : prev));
    }, [count]);

    useImperativeHandle(ref, () => ({ toggleVisibility }), [toggleVisibility]);

    const renderedLabel =
      typeof label !== 'function' ? (
        <Label className={classes.label} count={count} showCount={showCount} tooltipText={tooltipText}>
          {label}
        </Label>
      ) : (
        label(count)
      );

    return (
      <SimpleAccordion
        className={props.className}
        classes={classes}
        grows={grows}
        height={height}
        label={renderedLabel}
        open={open}
        toggleOpen={toggleVisibility}
      >
        {props.children}
      </SimpleAccordion>
    );
  }
);

export { Accordion };
export default Accordion;
